import { Box, Typography, Stack, useTheme, styled } from "@mui/material";
import { Error24Px } from "@qubit/autoparts";
import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";

import { resetAppReady, setSiteLoginPath } from "~/app/app.slice";
import { useAppDispatch, useAppSelector } from "~/app/store";
import AutoStoreLogo from "~/assets/img/autostore-dots-and-logo.svg?react";
import warehouseImage from "~/assets/img/autostore-warehouse.webp";
import { clearAndonWorkstationsAndGrids } from "~/features/andon/andon.slice";
import { OrganizationLoginButton } from "~/features/organizationLoginButton";

import BrowserCompatibilityWarning from "./BrowserCompatibilityWarning";
import LoginBranding from "./LoginBranding";
import { clearLoginState } from "./login.slice";

const loginButtonStyles = {
  width: "100%",
  maxWidth: "500px",
  height: 72,
  fontSize: "21px",
  borderRadius: "15px",
  py: "15px",
  mt: "90px"
};

const ErrorContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 28px 0 0 6px;
`;

const CompatibilityWrapper = styled(Box)`
  width: 100%;
  min-height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`;

/**
 *  Login page displayed when a user navigates to /login/:name.
 *  For all clients except BevClient.
 */
function OrganizationLogin() {
  const { name } = useParams<{ name: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { palette } = useTheme();

  const errorMessage = useAppSelector((state) => state.login.errorMessage);

  useEffect(() => {
    dispatch(setSiteLoginPath(location.pathname));
    dispatch(resetAppReady());
    dispatch(clearLoginState());
    dispatch(clearAndonWorkstationsAndGrids());
  }, [dispatch, location.pathname]);

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 3fr"
      height="100vh"
      width="100vw"
    >
      <Stack
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        sx={{
          my: { xs: "1rem", md: "2rem", lg: "3rem" },
          mx: { xs: "2rem", md: "4rem", lg: "6rem" }
        }}
      >
        <LoginBranding />

        <OrganizationLoginButton
          sx={loginButtonStyles}
          organizationName={name}
        />

        {errorMessage && (
          <ErrorContainer>
            <Error24Px style={{ fill: palette.error.main }} />
            <Typography sx={{ color: palette.error.main, fontSize: "1.4rem" }}>
              {errorMessage}
            </Typography>
          </ErrorContainer>
        )}
        <CompatibilityWrapper>
          <BrowserCompatibilityWarning />
        </CompatibilityWrapper>
        <AutoStoreLogo style={{ position: "absolute", bottom: "48px" }} />
      </Stack>

      <Box
        component="img"
        src={warehouseImage}
        alt="autostore warehouse"
        width="100%"
        height="100%"
        sx={{ objectFit: "cover" }}
      />
    </Box>
  );
}

export default OrganizationLogin;
